import { QueryHookOptions } from '@apollo/client';

import { snackbar } from '@occc-app/design-system';
import {
  LocationFilter,
  LocationFragment,
  LocationSort,
  LocationsQueryVariables,
  SiteManagementLocationFragment,
  SiteManagementLocationsQueryVariables,
  SitesSummaryQuery,
  SitesSummaryQueryVariables,
  useGetLocationSelectItemsLazyQuery,
  useLocationsLazyQuery,
  useLocationsQuery,
  useSiteManagementLocationQuery,
  useSiteManagementLocationsQuery,
  useSitesSummaryQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import {
  transformDataToLocationsArray,
  transformDataToSiteManagementLocationsArray,
} from '@occc-app/dataAccessLayer/location/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
import { useLazyQuery } from '../utils';

export const useGetLocationQuery = (
  uuid?: string,
  baseOptions?: QueryHookOptions<LocationFragment[], LocationsQueryVariables>,
) => {
  const response = useLocationsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !uuid,
    variables: {
      filter: {
        uuidEquals: uuid,
      },
    },
    ...baseOptions,
    onCompleted: (data) => {
      if (baseOptions && baseOptions.onCompleted) {
        baseOptions.onCompleted(transformDataToLocationsArray(data));
      }
    },
    onError: (error) => snackbar.error(getErrorMessage(error)),
  } as QueryHookOptions);

  return {
    ...response,
    data: response.data ? transformDataToLocationsArray(response.data)[0] : response.data,
  };
};

export const useGetLocationsLazyQuery = (
  baseOptions?: QueryHookOptions<LocationFragment[], LocationsQueryVariables>,
) => {
  const [executeQuery, response] = useLocationsLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...baseOptions,
    onCompleted: (data) => {
      if (baseOptions && baseOptions.onCompleted) {
        baseOptions.onCompleted(transformDataToLocationsArray(data));
      }
    },
  } as QueryHookOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformDataToLocationsArray(response.data) : response.data,
    },
  };
};

export const useGetLocationsQuery = (
  baseOptions?: QueryHookOptions<LocationFragment[], LocationsQueryVariables>,
) => {
  const response = useLocationsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...baseOptions,
    onCompleted: (data) => {
      if (baseOptions && baseOptions.onCompleted) {
        baseOptions.onCompleted(transformDataToLocationsArray(data));
      }
    },
  } as QueryHookOptions);

  return {
    ...response,
    data: response.data ? transformDataToLocationsArray(response.data) : response.data,
  };
};

type GetSiteManagementLocations = {
  filter: LocationFilter;
  sort?: LocationSort;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    SiteManagementLocationFragment[],
    SiteManagementLocationsQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetSiteManagementLocations = ({
  filter,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetSiteManagementLocations) => {
  const response = usePaginatedQuery(
    useSiteManagementLocationsQuery,
    'locations.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
        sort,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToSiteManagementLocationsArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToSiteManagementLocationsArray(response.data)
      : response.data,
  };
};

export const useGetLocationSummaryQuery = (locationId: string) => {
  const { data, ...rest } = useSiteManagementLocationQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: { uuidEquals: locationId },
    },
    onError: (error) => snackbar.error(getErrorMessage(error)),
  });

  return {
    ...rest,
    data: {
      location: data?.locations?.edges?.[0]?.node || null,
    },
  };
};

export const useGetSitesSummaryQuery = (
  baseOptions?: QueryHookOptions<SitesSummaryQuery, SitesSummaryQueryVariables>,
) => {
  const response = useSitesSummaryQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...baseOptions,
  });

  return {
    ...response,
    data: response.data ? response.data.sitesSummary : response.data,
  };
};

export const useGetLocationSelectItemsLazy = () => {
  const [executeQuery, response] = useLazyQuery(useGetLocationSelectItemsLazyQuery);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data?.locations?.edges?.map((e) => e.node),
    },
  };
};
